import { ProjectCsvMappingSection } from '@/features/projects';
import { useNotifications } from '@/providers/notificationHooks';
import { DialogSection, WizardDialog } from '@/ui';
import { Box, Button } from '@/ui/primitives';
import { HowFlowQaWorksDiagram } from './HowFlowQaWorksDiagram';
import { PostOnboardingTutorialThresholdSection } from './PostOnboardingTutorialThresholdSection';
import type { TutorialStepId } from './postOnboardingTutorialStorage';
import type { ReactElement } from 'react';

type PostOnboardingTutorialDialogProps = {
  open: boolean;
  currentStepId: TutorialStepId;
  currentStepIndex: number;
  totalSteps: number;
  projectId: string | null;
  onClose: () => void;
  onSkip: () => void;
  onBack: () => void;
  onNext: () => void;
  onComplete: () => void;
  onOpenTeam: () => void;
};

type TutorialStepConfig = {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  visual?: ReactElement | null;
};

const resolveStepConfig = (
  stepId: TutorialStepId,
  input: Pick<PostOnboardingTutorialDialogProps, 'projectId' | 'onOpenTeam'>,
): TutorialStepConfig => {
  switch (stepId) {
    case 'how-it-works':
      return {
        title: 'How FlowQA Works',
        description:
          'FlowQA turns uploaded result files into execution runs, highlights quality trends, and shows the signals that need attention.',
        visual: <HowFlowQaWorksDiagram />,
      };
    case 'mapping':
      return {
        title: 'Check CSV Patterns',
        description:
          'Review your CSV/XLSX mapping so uploaded spreadsheet columns line up with what FlowQA expects.',
      };
    case 'thresholds':
      return {
        title: 'Set Metric Thresholds',
        description:
          'Define target and warning thresholds so FlowQA can show clearer quality signals in your project.',
      };
    case 'invite':
      return {
        title: 'Invite Your Team',
        description:
          'Invite people into the workspace so results, thresholds, and settings are shared.',
        actionLabel: 'Manage Team',
        onAction: input.onOpenTeam,
      };
  }
};

export const PostOnboardingTutorialDialog = ({
  open,
  currentStepId,
  currentStepIndex,
  totalSteps,
  projectId,
  onClose,
  onSkip,
  onBack,
  onNext,
  onComplete,
  onOpenTeam,
}: PostOnboardingTutorialDialogProps): ReactElement => {
  const { notify } = useNotifications();
  const step = resolveStepConfig(currentStepId, { projectId, onOpenTeam });

  return (
    <WizardDialog
      open={open}
      onClose={onClose}
      title={step.title}
      description={step.description}
      currentStepIndex={currentStepIndex}
      totalSteps={totalSteps}
      skipLabel="Skip for Now"
      finishLabel="Finish Tutorial"
      closeAriaLabel="Close tutorial"
      onSkip={onSkip}
      onBack={onBack}
      onNext={onNext}
      onFinish={onComplete}
    >
      <DialogSection gap="lg">
        {step.actionLabel && step.onAction ? (
          <Box>
            <Button variant="secondary" fullWidthOnMobile={false} onClick={step.onAction}>
              {step.actionLabel}
            </Button>
          </Box>
        ) : null}
        {step.visual}
        {currentStepId === 'mapping' && projectId !== null ? (
          <ProjectCsvMappingSection projectId={projectId} notify={notify} container="embedded" />
        ) : null}
        {currentStepId === 'thresholds' ? (
          <PostOnboardingTutorialThresholdSection projectId={projectId} />
        ) : null}
      </DialogSection>
    </WizardDialog>
  );
};
